import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { ArticleCard, ArticleListItem, categoryName } from "../components/ArticleCard";
import MetaTags from "../lib/MetaTags";
import { fetchFeatured, fetchLatest, fetchCategories } from "../lib/api";
import { CATEGORIES, SITE } from "../lib/categories";
import { ArrowRight, MapPin } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/20649748/pexels-photo-20649748.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    fetchFeatured(5).then(setFeatured).catch(() => {});
    fetchLatest(8).then(setLatest).catch(() => {});
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  const hero = featured[0];
  const secondary = featured.slice(1, 5);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    logo: `${SITE.url}/logo.png`,
    areaServed: "Chicago, Illinois",
  };
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.url,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Layout>
      <MetaTags
        title="Chicago's electronics repair, buyback, resale & recycling guide"
        description={SITE.description}
        image={HERO_IMG}
        schema={[orgSchema, websiteSchema]}
      />

      {/* Hero */}
      <section className="border-b border-[#121212] bg-white">
        <div className="ceg-container py-10 md:py-16 grid grid-cols-12 gap-6 md:gap-8">
          <div className="col-span-12 md:col-span-7 ceg-fade-up">
            <p className="ceg-overline">Chicago · Independent · Editorial</p>
            <h1 className="font-['Outfit'] font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-3">
              The local<br />guide to<br />
              <span className="text-[#0055FF]">repairing, selling</span><br />
              & <span className="bg-[#FF3823] text-white px-2 inline-block">recycling</span> electronics.
            </h1>
            <p className="mt-6 text-base sm:text-lg md:text-xl text-[#5C5C5C] max-w-xl leading-relaxed">
              Real repair costs, honest resale prices, refurbished buying advice, and step-by-step prep guides for everyone in Chicagoland with a phone, laptop, tablet, or console.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/category/device-repair-guides" className="ceg-btn-primary" data-testid="hero-repair-cta">
                Repair cost guides <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link to="/category/buyback-price-guides" className="ceg-btn-secondary" data-testid="hero-prices-cta">
                Check resale prices
              </Link>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5">
            {hero ? (
              <Link to={`/article/${hero.slug}`} className="block group" data-testid="hero-featured">
                <div className="overflow-hidden border border-[#121212] relative">
                  <img src={hero.hero_image} alt={hero.title} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 bg-[#FF3823] text-white text-[10px] uppercase tracking-wider px-2 py-1 font-bold">
                    Featured · {categoryName(hero.category)}
                  </span>
                </div>
                <h2 className="mt-4 font-['Outfit'] font-bold text-2xl md:text-3xl tracking-tight leading-tight group-hover:text-[#0055FF]">
                  {hero.title}
                </h2>
                <p className="mt-2 text-sm text-[#5C5C5C] line-clamp-2">{hero.excerpt}</p>
              </Link>
            ) : (
              <div className="aspect-[4/3] bg-[#F0F0F0] border border-[#E5E5E5]" />
            )}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[#121212] text-white">
        <div className="ceg-container py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
          {[
            { n: "20+", l: "In-depth guides" },
            { n: "11", l: "Topic categories" },
            { n: "100%", l: "Chicago focused" },
            { n: "Free", l: "Always" },
          ].map((s, i) => (
            <div key={i} className="border-l-2 border-[#FF3823] pl-3" data-testid={`stat-${i}`}>
              <p className="font-['Outfit'] font-black text-3xl md:text-4xl tracking-tighter">{s.n}</p>
              <p className="text-xs uppercase tracking-wider text-white/70 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured grid */}
      {secondary.length > 0 && (
        <section className="border-b border-[#121212]">
          <div className="ceg-container py-12 md:py-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="ceg-overline">Editor's picks</p>
                <h2 className="font-['Outfit'] font-black tracking-tighter text-4xl md:text-5xl mt-2">Featured guides</h2>
              </div>
              <Link to="/search" className="text-sm uppercase tracking-wider font-semibold underline underline-offset-4 decoration-[#FF3823]" data-testid="featured-view-all">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-12 gap-6">
              {secondary.map((a, i) => (
                <div key={a.slug} className={i === 0 ? "col-span-12 md:col-span-8" : "col-span-12 md:col-span-4"}>
                  <ArticleCard article={a} size={i === 0 ? "lg" : "md"} index={i} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories grid */}
      <section className="bg-white border-b border-[#121212]">
        <div className="ceg-container py-12 md:py-16">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <p className="ceg-overline">Browse by topic</p>
              <h2 className="font-['Outfit'] font-black tracking-tighter text-4xl md:text-5xl mt-2">Categories</h2>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-px bg-[#121212] border border-[#121212]">
            {(cats.length ? cats : CATEGORIES).map((c, i) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="col-span-12 sm:col-span-6 lg:col-span-4 bg-white p-6 hover:bg-[#121212] hover:text-white transition-colors group"
                data-testid={`category-tile-${c.slug}`}
              >
                <p className="ceg-overline group-hover:text-white">{c.article_count ?? ""} {c.article_count ? "guides" : ""}</p>
                <h3 className="font-['Outfit'] font-bold text-2xl tracking-tight mt-2">{c.name}</h3>
                {c.description && <p className="text-sm text-[#5C5C5C] group-hover:text-white/70 mt-2">{c.description}</p>}
                <p className="mt-4 text-xs uppercase tracking-wider font-semibold flex items-center gap-1">
                  Explore <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest articles */}
      <section className="border-b border-[#121212]">
        <div className="ceg-container py-12 md:py-16 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6 border-b border-[#121212] pb-4">
              <div>
                <p className="ceg-overline">Just published</p>
                <h2 className="font-['Outfit'] font-black tracking-tighter text-3xl md:text-4xl mt-1">Latest guides</h2>
              </div>
            </div>
            <div>
              {latest.slice(0, 6).map((a, i) => (
                <ArticleListItem key={a.slug} article={a} index={i} />
              ))}
            </div>
          </div>
          <aside className="col-span-12 md:col-span-4 space-y-6">
            <div className="bg-[#121212] text-white p-6">
              <p className="ceg-overline text-white">Why we exist</p>
              <h3 className="font-['Outfit'] font-black text-2xl tracking-tighter mt-2 leading-tight">
                Chicago<br />deserves<br />honest tech<br />advice<span className="text-[#FF3823]">.</span>
              </h3>
              <p className="text-sm text-white/70 mt-4 leading-relaxed">
                We aren't a buyback shop. We're not affiliated with any one buyer. We test, compare, and write so you don't get lowballed.
              </p>
              <Link to="/about" className="mt-4 inline-block text-xs uppercase tracking-wider font-semibold underline underline-offset-4 decoration-[#FF3823]" data-testid="home-about-link">
                Read the mission →
              </Link>
            </div>
            <div className="border border-[#121212] p-6">
              <p className="ceg-overline flex items-center gap-2"><MapPin size={12} /> Local resource</p>
              <h3 className="font-['Outfit'] font-bold text-xl mt-2">Recycling & Repair Directory</h3>
              <p className="text-sm text-[#5C5C5C] mt-2">Our independent directory of Chicago electronics recycling drop-offs, donation programs, and device repair options across the city.</p>
              <Link to="/directory" className="mt-3 inline-block text-xs uppercase tracking-wider font-semibold underline underline-offset-4 decoration-[#0055FF]" data-testid="home-directory-link">
                Browse the directory →
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
